import { XCircle } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { LostReason } from '@/features/crm/deals/constants';
import { LostReasonSelect } from '@/features/crm/deals/components/LostReasonSelect';
import { useDeal } from '@/features/crm/deals/queries';
import { useRejectProposal } from '../queries';

/** "Mark lost" action: rejects the proposal and loses the linked deal, capturing
 *  a reason. The inverse of "Accept → create project". */
export function RejectProposalDialog({
  proposalId,
  dealId,
}: {
  proposalId: string | number;
  /** The proposal's deal; rejecting closes it to Lost while it's still open. */
  dealId: number | null;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [lostReason, setLostReason] = useState<LostReason | ''>('');
  const reject = useRejectProposal();
  const deal = useDeal(open && dealId ? dealId : '');
  // A lost reason is asked only when this closes an open deal (ADR-108);
  // while the deal is loading, ask anyway rather than send none.
  const closesDeal = dealId !== null && (deal.data?.is_open ?? true);

  async function handleConfirm() {
    if (closesDeal && !lostReason) return;
    // The free-text reason doubles as the deal's lost note (ADR-108).
    await reject.mutateAsync({
      id: proposalId,
      reason: reason.trim(),
      ...(closesDeal && lostReason ? { lost_reason: lostReason } : {}),
    });
    setOpen(false);
    setReason('');
    setLostReason('');
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <XCircle /> Mark lost
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark this proposal lost?</DialogTitle>
          <DialogDescription>
            {closesDeal
              ? 'This rejects the proposal and moves the linked deal to the Lost stage. Add a reason for the record.'
              : 'This rejects the proposal. Add a reason for the record.'}
          </DialogDescription>
        </DialogHeader>
        {closesDeal ? (
          <div className="space-y-2">
            <Label htmlFor="reject-lost-reason">Why was it lost?</Label>
            <LostReasonSelect
              id="reject-lost-reason"
              aria-label="Lost reason"
              value={lostReason}
              onChange={setLostReason}
            />
          </div>
        ) : null}
        <div className="space-y-2">
          <Label htmlFor="reject-reason">Details</Label>
          <Textarea
            id="reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Budget cut, chose a competitor, project cancelled…"
            rows={4}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={reject.isPending || (closesDeal && !lostReason)}
          >
            {reject.isPending ? 'Marking…' : 'Mark lost'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
